import React, { FC, useCallback, useMemo, useState } from "react";
import { StandardModal } from "../../../general/Modal/Modal";
import { DocumentsModal } from "../documentsModal/DocumentsModal";
import { useWorkspaceContext } from "../../../../state/workspaceContext";
import { useFinancialDocumentUpload } from "../../../documents/FinancialDocumentUpload/useFinancialDocumentUpload";
import { useRemoveFinancialDocumentMutation } from "../../../../mutations/financialDocument";
import { useFinancialDocumentsQuery } from "../../../documents/useFinancialDocumentsQuery";
import { UploadingDocumentPlaceholder } from "../../../documents/lib";
import { FinancialDocumentDetails } from "../../../documents/FinancialDocumentDetails/FinancialDocumentDetails";
import { FinancialDocumentDetailsView } from "../../../documents/FinancialDocumentDetails/usePathBasedFinancialDocumentDetailsView";
import { financialDocumentDetailsContext } from "../../../documents/FinancialDocumentDetails/financialDocumentDetails.context";
import { UploadReceipts } from "./UploadedReceipts";

export interface UploaderSectionBodyProps {
    onFinancialDocumentStatusUpdated(): void;
}

export const UploaderSectionBody: FC<UploaderSectionBodyProps> = ({
    onFinancialDocumentStatusUpdated,
}) => {
    const [showAllDocuments, setShowAllDocuments] = useState(false);
    const { activeWorkspaceKey } = useWorkspaceContext();
    const { uploadingDocuments, updateUploads } = useFinancialDocumentUpload();
    const handleRemoveDocument = useRemoveFinancialDocumentMutation();
    const [financialDocumentForDetailsId, setFinancialDocumentForDetailsId] =
        useState<number | null>(null);

    const [documentDetailsView, setDocumentDetailsView] =
        useState<FinancialDocumentDetailsView>(
            FinancialDocumentDetailsView.DEFAULT,
        );

    const financialDocumentsForStatusCheck = useMemo(() => {
        const documentsForStatusCheck: number[] = [];

        for (const { isUploading, financialDocument } of uploadingDocuments) {
            if (
                isUploading ||
                !financialDocument // not done uploading yet
            ) {
                continue;
            }

            documentsForStatusCheck.push(financialDocument.id);
        }

        return documentsForStatusCheck;
    }, [uploadingDocuments]);

    const { data: financialDocumentsResponse } = useFinancialDocumentsQuery({
        queryParams: {
            page: 0,
            limit: uploadingDocuments.length,
            workspace: activeWorkspaceKey,
            filters: {
                financialDocumentIds: financialDocumentsForStatusCheck,
            },
        },
        shouldRefetch: () => financialDocumentsForStatusCheck.length > 0,
        onSuccess: onFinancialDocumentStatusUpdated,
    });

    const refreshedItems = useMemo(
        () =>
            uploadingDocuments.map((item) => {
                if (!item.financialDocument) {
                    return item;
                }

                const refreshedItem = financialDocumentsResponse?.data.find(
                    (document) => document.id === item.financialDocument?.id,
                );

                return {
                    ...item,
                    financialDocument: refreshedItem ?? item.financialDocument,
                };
            }),
        [financialDocumentsResponse?.data, uploadingDocuments],
    );

    const handleRemove = useCallback(
        async (item: UploadingDocumentPlaceholder) => {
            if (!item.financialDocument) {
                return;
            }

            await handleRemoveDocument.mutateAsync(item.financialDocument);
            updateUploads((prev) =>
                prev.filter(
                    (el) =>
                        el.financialDocument?.id !== item.financialDocument?.id,
                ),
            );
        },
        [handleRemoveDocument, updateUploads],
    );

    const contextValue = useMemo(
        () => ({
            currentView: documentDetailsView,
            changeView: setDocumentDetailsView,
        }),
        [documentDetailsView],
    );

    const financialDocumentForDetails = useMemo(() => {
        if (!financialDocumentForDetailsId) {
            return null;
        }

        return refreshedItems.find(
            (item) =>
                item.financialDocument?.id === financialDocumentForDetailsId,
        )?.financialDocument;
    }, [financialDocumentForDetailsId, refreshedItems]);

    return (
        <>
            <UploadReceipts
                items={refreshedItems}
                onViewAll={() => setShowAllDocuments(true)}
                onRemove={handleRemove}
            />

            <StandardModal
                show={uploadingDocuments.length > 0 && showAllDocuments}
                onHide={() => setShowAllDocuments(false)}
                size="xl"
            >
                <DocumentsModal
                    items={refreshedItems}
                    onRemove={handleRemove}
                    onDetails={(item) =>
                        setFinancialDocumentForDetailsId(
                            item.financialDocument?.id ?? null,
                        )
                    }
                />
            </StandardModal>

            <StandardModal
                className="fullscreen-modal"
                show={!!financialDocumentForDetails}
                onHide={() => setFinancialDocumentForDetailsId(null)}
            >
                {financialDocumentForDetails && (
                    <financialDocumentDetailsContext.Provider
                        value={contextValue}
                    >
                        <FinancialDocumentDetails
                            financialDocument={financialDocumentForDetails}
                        />
                    </financialDocumentDetailsContext.Provider>
                )}
            </StandardModal>
        </>
    );
};
